import * as echarts from 'echarts';

/**
 * Captura un gráfico ECharts como imagen (data URL)
 * @param {Object} chartConfig - Configuración del gráfico (chart object con chartType, title, data, etc.)
 * @param {number} width - Ancho en píxeles (default: 800)
 * @param {number} height - Alto en píxeles (default: 400)
 * @returns {Promise<string>} Data URL de la imagen del gráfico
 */
export const captureChartAsImage = async (chartConfig, width = 800, height = 400) => {
  return new Promise((resolve, reject) => {
    try {
      // Crear contenedor temporal fuera del viewport
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      container.style.width = `${width}px`;
      container.style.height = `${height}px`;
      container.style.visibility = 'hidden';

      document.body.appendChild(container);

      // Inicializar ECharts
      const myChart = echarts.init(container);

      // Construir la opción del gráfico (reutilizar lógica de ChartIAInsight)
      const option = buildChartOption(chartConfig);

      // Establecer la opción
      myChart.setOption(option);

      // Esperar a que se renderice
      setTimeout(() => {
        try {
          // Obtener imagen como data URL
          const dataUrl = myChart.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          });

          // Limpiar
          myChart.dispose();
          document.body.removeChild(container);

          resolve(dataUrl);
        } catch (error) {
          myChart.dispose();
          document.body.removeChild(container);
          reject(error);
        }
      }, 500);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Construye la opción de ECharts basada en la configuración del artifact
 * (Replica la lógica de ChartIAInsight)
 */
const buildChartOption = (chart) => {
  if (!chart) return {};

  const labels = chart.data.map((item) => item.label);
  const values = chart.data.map((item) => Number(item.value || 0));

  let option = {
    backgroundColor: 'transparent',
    title: {
      text: chart.title || '',
      left: 20,
      top: 12,
      textStyle: {
        fontSize: 15,
        fontWeight: 700,
        color: '#1f2937',
      },
    },
    tooltip: {
      show: true,
      trigger: 'axis',
    },
    grid: {
      top: 70,
      left: 72,
      right: 32,
      bottom: 72,
    },
    xAxis: {
      type: 'category',
      name: chart.xLabel || '',
      data: labels,
      axisLine: {
        lineStyle: {
          color: '#cbd5e1',
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: '#64748b',
        rotate: labels.some((label) => label.length > 15) ? 18 : 0,
        fontSize: 11,
      },
      nameTextStyle: {
        color: '#64748b',
        padding: [12, 0, 0, 0],
      },
    },
    yAxis: {
      type: 'value',
      name: chart.yLabel || '',
      splitLine: {
        lineStyle: {
          color: '#eef2f7',
        },
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: '#64748b',
        fontSize: 11,
      },
      nameTextStyle: {
        color: '#64748b',
        padding: [0, 0, 8, 0],
      },
    },
    series: [],
    animation: false,
  };

  if (chart.chartType === 'line') {
    option.series = [
      {
        type: 'line',
        data: values,
        smooth: true,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: {
          width: 3,
          color: '#2563eb',
        },
        itemStyle: {
          color: '#2563eb',
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(37, 99, 235, 0.22)' },
              { offset: 1, color: 'rgba(37, 99, 235, 0.02)' },
            ],
          },
        },
      },
    ];
  } else if (chart.chartType === 'bar') {
    option.series = [
      {
        type: 'bar',
        data: values,
        barWidth: '42%',
        itemStyle: {
          borderRadius: [8, 8, 0, 0],
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: '#2563eb' },
              { offset: 1, color: '#93c5fd' },
            ],
          },
        },
      },
    ];
  } else if (chart.chartType === 'pie') {
    option = {
      title: {
        text: chart.title || '',
        left: 'center',
        textStyle: {
          fontSize: 14,
          fontWeight: 600,
        },
      },
      series: [
        {
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['50%', '56%'],
          data: chart.data.map((item) => ({
            name: item.label,
            value: Number(item.value || 0),
          })),
          label: {
            formatter: '{b}\n{d}%',
            color: '#475569',
            fontSize: 11,
          },
          itemStyle: {
            borderRadius: 8,
            borderColor: '#ffffff',
            borderWidth: 3,
          },
        },
      ],
      animation: false,
    };
  }

  return option;
};

/**
 * Captura múltiples gráficos en paralelo
 */
export const captureChartsAsImages = async (charts, width = 800, height = 400) => {
  const promises = charts.map((chart) =>
    captureChartAsImage(chart, width, height).catch((error) => {
      console.error('Error capturando gráfico:', error);
      return null; // Devolver null si falla, para no romper el flujo
    })
  );

  const images = await Promise.all(promises);
  return images.filter((img) => img !== null); // Filtrar nulls
};
